import React, { FC, useEffect, useRef, useState } from 'react';
import { ref, onValue, query, orderByChild, limitToLast, update, Unsubscribe } from 'firebase/database';
import { database } from '../../lib/firebase';

interface EscalationStatus {
  event: string;
  details: string;
  timestamp: number;
}

interface ActiveEscalation {
  escalationId: string;
  seniorId: string;
  seniorName: string;
  currentStatus: EscalationStatus;
}

interface EscalationAlertProps {
  seniorIds: string[];
}

const TEN_MINUTES_MS = 10 * 60 * 1000;

export const EscalationAlert: FC<EscalationAlertProps> = ({ seniorIds }) => {
  const [escalations, setEscalations] = useState<ActiveEscalation[]>([]);
  const notifiedEscalationIds = useRef<Set<string>>(new Set());
  const audioContextRef = useRef<AudioContext | null>(null);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Clean up AudioContext on unmount
  useEffect(() => {
    return () => {
      audioContextRef.current?.close();
      audioContextRef.current = null;
    };
  }, []);

  // Listen for escalation events from Firebase RTDB
  useEffect(() => {
    if (seniorIds.length === 0) {
      setEscalations([]);
      return;
    }

    const unsubscribes: Unsubscribe[] = [];

    seniorIds.forEach((seniorId) => {
      const escalationsRef = query(
        ref(database, `devices/${seniorId}/escalations`),
        orderByChild('currentStatus/timestamp'),
        limitToLast(5)
      );

      const unsub = onValue(escalationsRef, (snapshot) => {
        // Build full list of active escalations for this senior
        const activeForSenior: ActiveEscalation[] = [];

        if (snapshot.exists()) {
          snapshot.forEach((child) => {
            const escId = child.key;
            const data = child.val();
            if (!escId || !data?.currentStatus) return;

            const status = data.currentStatus;
            // Validate Firebase data shape
            if (typeof status.event !== 'string' || typeof status.details !== 'string' || typeof status.timestamp !== 'number') return;

            // F03/F04 — caregiver dismissal persists in Firebase so the toast
            // doesn't reappear on every page load. Skip anything with a
            // dismissed_at field regardless of age.
            if (typeof data.dismissed_at === 'number') return;

            // Android writes Firebase ServerValue.TIMESTAMP, which is ms since
            // epoch — NOT seconds. The earlier `* 1000` made `age` negative for
            // any real timestamp, defeating both the 10-min window and the
            // 60s post-cancel filter, so cancelled toasts persisted forever.
            const age = Date.now() - status.timestamp;

            // Only show escalations from last 10 minutes
            if (age > TEN_MINUTES_MS) return;

            // Skip already-resolved/cancelled old ones (show briefly for 60s)
            if ((status.event === 'cancelled' || status.event === 'resolved') && age > 60_000) return;

            activeForSenior.push({
              escalationId: escId,
              seniorId,
              seniorName: status.details.split(' ').slice(-1)[0] || 'Senior',
              currentStatus: status as EscalationStatus,
            });
          });
        }

        // Replace escalations for this senior (removes stale ones)
        setEscalations((prev) => {
          const withoutThisSenior = prev.filter((e) => e.seniorId !== seniorId);

          // Trigger notifications for truly new escalations
          activeForSenior.forEach((esc) => {
            if (!notifiedEscalationIds.current.has(esc.escalationId)) {
              notifiedEscalationIds.current.add(esc.escalationId);
              playUrgentAlertSound();
              showBrowserNotification(esc);
            }
          });

          return [...activeForSenior, ...withoutThisSenior];
        });
      }, (error) => {
        console.error('Escalation Firebase error:', error.message);
      });

      unsubscribes.push(unsub);
    });

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(seniorIds)]);

  const playUrgentAlertSound = async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    const ctx = audioContextRef.current;

    // Resume if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch { return; }
    }

    // Three urgent beeps
    [0, 0.3, 0.6].forEach((delay) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 1000;
      osc.type = 'square';
      gain.gain.setValueAtTime(0.3, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.2);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.2);
    });
  };

  const showBrowserNotification = (escalation: ActiveEscalation) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notif = new Notification('🚨 URGENT: Emergency Escalation', {
        body: `${escalation.currentStatus.details}`,
        icon: '/logo192.png',
        tag: escalation.escalationId,
        requireInteraction: true,
        vibrate: [300, 100, 300, 100, 300],
      } as NotificationOptions);
      notif.onclick = () => { window.focus(); notif.close(); };
    }
  };

  const dismissEscalation = (esc: ActiveEscalation) => {
    // Hide locally right away so the toast disappears with no perceived lag.
    setEscalations((prev) => prev.filter((e) => e.escalationId !== esc.escalationId));
    // Persist dismissal in Firebase so the next snapshot / page load / other
    // device doesn't re-surface the same toast. We use a child UPDATE (not
    // a delete) so the events audit trail under escalations/{eid}/events
    // stays intact for forensic review.
    try {
      update(ref(database, `devices/${esc.seniorId}/escalations/${esc.escalationId}`), {
        dismissed_at: Date.now(),
      }).catch((err) => {
        console.warn('EscalationAlert: failed to persist dismissal', err);
      });
    } catch (err) {
      console.warn('EscalationAlert: dismiss write threw', err);
    }
  };

  const getStatusColor = (event: string): string => {
    switch (event) {
      case 'calling_911': return 'bg-red-700';
      case 'calling_caregiver': return 'bg-orange-600';
      case 'started': return 'bg-yellow-600';
      case 'resolved': return 'bg-green-600';
      case 'cancelled': return 'bg-gray-600';
      case 'no_answer': return 'bg-orange-700';
      default: return 'bg-red-600';
    }
  };

  const getStatusIcon = (event: string): string => {
    switch (event) {
      case 'calling_911': return 'emergency';
      case 'calling_caregiver': return 'call';
      case 'started': return 'timer';
      case 'resolved': return 'check_circle';
      case 'cancelled': return 'cancel';
      case 'no_answer': return 'phone_missed';
      default: return 'warning';
    }
  };

  const getStatusLabel = (event: string): string => {
    switch (event) {
      case 'calling_911': return 'CALLING 911';
      case 'calling_caregiver': return 'Calling Caregiver';
      case 'started': return 'Escalation Started';
      case 'resolved': return 'Resolved';
      case 'cancelled': return 'Cancelled';
      case 'no_answer': return 'No Answer';
      default: return 'Emergency';
    }
  };

  const formatTimestamp = (ts: number): string => {
    const date = new Date(ts * 1000);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  if (escalations.length === 0) return null;

  return (
    // Toast stack — top-right on desktop, top-edge on mobile. Cards are
    // pointer-events-auto so background clicks pass through the wrapper.
    <div className="fixed z-[1100] space-y-3 pointer-events-none
                    top-3 right-3 left-3 sm:left-auto sm:top-4 sm:right-4
                    sm:w-[360px] max-w-[calc(100vw-1.5rem)]">
      {escalations.map((esc) => (
        <div
          key={esc.escalationId}
          className={`${getStatusColor(esc.currentStatus.event)} text-white rounded-xl shadow-2xl
                      p-4 pointer-events-auto animate-slideInRight
                      ${esc.currentStatus.event === 'calling_911' ? 'animate-pulse ring-4 ring-red-400' : ''}`}
          role="alert"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-2xl">
                {getStatusIcon(esc.currentStatus.event)}
              </span>
              <h3 className="font-bold text-lg">{getStatusLabel(esc.currentStatus.event)}</h3>
            </div>
            <button
              onClick={() => dismissEscalation(esc)}
              className="text-white hover:text-gray-200 transition-colors"
              aria-label="Dismiss"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          <div className="space-y-1 text-sm">
            <p className="font-semibold text-base">{esc.currentStatus.details}</p>
            <p className="opacity-80">{formatTimestamp(esc.currentStatus.timestamp)}</p>
          </div>

          {(esc.currentStatus.event === 'resolved' || esc.currentStatus.event === 'cancelled') && (
            <button
              onClick={() => dismissEscalation(esc)}
              className="mt-3 w-full bg-white text-gray-700 font-semibold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Dismiss
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default EscalationAlert;
